import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useBoolean } from 'usehooks-ts';
import { isEmpty, noop, size } from 'lodash';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { faExternalLinkSquare } from '@fortawesome/pro-regular-svg-icons';
import { Button, createBemBlockBuilder, Hyperlink } from '@ebsco-ui/ebsco-ui';

import { Icon } from '@app/components';

import { DownloadFormat } from '../../constants';
import { DownloadMarcModal } from './DowloadMarcModal';
import { isMarcFormat } from './utils';

import './DownloadMarc.scss';

interface DownloadMarcProps {
  formats: DownloadFormat[];
}

const cnBem = createBemBlockBuilder(['downloadMarc']);

export const DownloadMarc: FC<DownloadMarcProps> = ({ formats }) => {
  const { $t } = useIntl();
  const {
    value: isModalOpen,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean(false);

  if (isEmpty(formats)) {
    return null;
  }

  const areMultipleFormats = size(formats) > 1;

  const renderDownloadControl = () => {
    const firstFormat = formats[0];
    const isSingleMarcDownload =
      isMarcFormat(firstFormat.name) && !areMultipleFormats;
    const isButton = areMultipleFormats || isSingleMarcDownload;

    if (isButton) {
      return (
        <Button
          styleType="passive"
          className={cnBem('__downloadButton')}
          onClick={areMultipleFormats ? openModal : noop}
        >
          <Icon icon={faDownload} />
          {areMultipleFormats
            ? $t({
                id: 'instanceDetails.download',
                defaultMessage: 'Download',
              })
            : $t({
                id: 'instanceDetails.downloadMarc',
                defaultMessage: 'Download MARC',
              })}
        </Button>
      );
    }

    return (
      <Hyperlink
        styleType="passive"
        className={cnBem('__downloadButton')}
        href={firstFormat.link}
        target="_blank"
      >
        <Icon icon={faExternalLinkSquare} />
        {firstFormat.name}
      </Hyperlink>
    );
  };

  return (
    <>
      {renderDownloadControl()}
      {areMultipleFormats && (
        <DownloadMarcModal
          isOpen={isModalOpen}
          formats={formats}
          onClose={closeModal}
        />
      )}
    </>
  );
};
